import { ApiProblem, type Api, type Schema } from '@cdn/api';

interface Held {
	timer: ReturnType<typeof setTimeout> | undefined;
	renewed: (grant: Schema.RealtimeGrant) => void;
	failed: (problem: unknown) => void;
	/** Consecutive renewals that were not answered */
	failures: number;
}

/**
 * Short-lived realtime grants from `POST /v1/realtime/grants`. A grant is renewed before it expires, at four
 * fifths of its lifetime, and the holder is told the new token; when the renewal is refused the holder tears
 * its subscription down.
 *
 * Only a refusal (`ACCESS_REVOKED`, `FORBIDDEN`, `NOT_FOUND`) ends the subscription. A renewal the service did
 * not answer is an outage: it is asked again after a wait that doubles up to 30 seconds, and the stream's own
 * recovery covers whatever was missed meanwhile.
 */
export class Grants {
	#api: Api;
	#held = new Map<string, Held>();

	constructor(api: Api) {
		this.#api = api;
	}

	/**
	 * @param renewed Runs with every renewed grant of the channel
	 * @param failed Runs when a renewal was refused
	 */
	async obtain(channel: string, renewed: Held['renewed'], failed: Held['failed']): Promise<Schema.RealtimeGrant> {
		this.release(channel);
		const held: Held = { timer: undefined, renewed, failed, failures: 0 };
		this.#held.set(channel, held);
		const grant = await this.#api.call('realtime.grant', { body: { channels: [channel] } });
		this.#held.get(channel) === held && this.#schedule(channel, held, grant);
		return grant;
	}

	release(channel: string) {
		clearTimeout(this.#held.get(channel)?.timer);
		this.#held.delete(channel);
	}

	clear() {
		for (const channel of [...this.#held.keys()]) this.release(channel);
	}

	#schedule(channel: string, held: Held, grant: Schema.RealtimeGrant) {
		const lifetime = new Date(grant.expires).getTime() - Date.now();
		held.timer = setTimeout(() => void this.#renew(channel, held), Math.max(1000, lifetime * 0.8));
	}

	async #renew(channel: string, held: Held) {
		try {
			const grant = await this.#api.call('realtime.grant', { body: { channels: [channel] } });
			if (this.#held.get(channel) !== held) return;
			held.failures = 0;
			held.renewed(grant);
			this.#schedule(channel, held, grant);
		} catch (problem) {
			if (this.#held.get(channel) !== held) return;
			if (ApiProblem.of(problem).is('ACCESS_REVOKED', 'FORBIDDEN', 'NOT_FOUND')) return held.failed(problem);
			held.timer = setTimeout(() => void this.#renew(channel, held), Math.min(3000 * 2 ** held.failures++, 30000));
		}
	}
}
